import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import { AppRadioButton } from '../../../general/AppRadioButton/AppRadioButton';
import { AppDropDown } from '../../../general/AppDropDown/AppDropDown';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import {
  CreditApplicationFields,
  CreditApplicationStepField,
  GroupedCreditApplicationFields,
} from '@dill/dill-shared';
import { Trash } from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import RadioInputs from './RadioInput';
import { AppButton } from '../../../general/AppButton/AppButton';
import { DillCheckbox } from '../../../general/Dill/DillCheckbox';

type FieldErrorState = {
  [fieldKey: string]: {
    name?: string;
    id?: string;
  };
};

interface FormDocumentFieldsProps {
  onChangeConditionalField: (
    data: Record<string, Record<string, CreditApplicationStepField | null>> | null
  ) => void;
  fieldId: string;
  valueConditionalFields: Record<string, Record<string, CreditApplicationStepField | null>> | null;
  filteredStardardFieldIds: string[];
  currentStepFieldIds: string[];
}

const FIELD_TYPE = [
  'TEXT',
  'NUMBER',
  'CURRENCY',
  'DATE',
  'TEXT_AREA',
  'FILE',
  'DOCUMENT_FIELD',
  'OPTION_FIELD',
  'CONDITIONAL_OPTION',
  'STATE_DROPDOWN',
  'CHECKBOX_LIST',
  'DROPDOWN',
];
const COLUMN_TYPES = ['ONE_COLUMN', 'TWO_COLUMNS', 'THREE_COLUMNS'];

export interface FormDocumentFieldsRef {
  validate: () => boolean;
  onSubmit: () => Record<string, CreditApplicationStepField | null> | null;
}

const FormDocumentFields = forwardRef<FormDocumentFieldsRef, FormDocumentFieldsProps>(
  (
    {
      onChangeConditionalField,
      fieldId,
      valueConditionalFields,
      filteredStardardFieldIds,
      currentStepFieldIds,
    },
    ref
  ) => {
    const [groupType, setGroupType] = useState<'GROUPED' | 'NOT_GROUPED'>('GROUPED');
    const [errorFields, setErrorFields] = useState<string[]>([]);

    const [groupedFields, setGroupedFields] = useState<GroupedCreditApplicationFields[]>([]);

    const [fieldErrors, setFieldErrors] = useState<FieldErrorState>({});

    const [fieldColumn, setFieldColumn] = useState<'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS'>(
      'ONE_COLUMN'
    );
    const [isIdAutoCompleteOpen, setIsIdAutoCompleteOpen] = useState('');
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
          setIsIdAutoCompleteOpen('');
        }
      }

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [setIsIdAutoCompleteOpen]);

    const filteredIdSuggestionsOptions = (text: string) => {
      return filteredStardardFieldIds.filter((option) =>
        option.toLowerCase().includes(text.trim().toLowerCase())
      );
    };

    const filterInviteUserFields = (fields: CreditApplicationFields[]) => {
      return fields.map((field) => {
        return field;
      });
    };

    useEffect(() => {
      if (!valueConditionalFields) return;

      const [outerKey, options] = Object.entries(valueConditionalFields)[0] ?? [];
      if (!options) return;

      for (const key in options) {
        const field = options[key];
        if (!field) continue;

        if (field.columnType) {
          setFieldColumn(field.columnType);
        }
        if (field.groupedFields?.length) {
          setGroupedFields(field.groupedFields ?? []);
        }
      }
    }, [valueConditionalFields]);

    useEffect(() => {
      const filteredGroupedFields =
        groupType === 'GROUPED'
          ? (groupedFields ?? []).map((group) => ({
              ...group,
              fields: filterInviteUserFields(group.fields),
            }))
          : [];

      const optionData = {
        columnType: fieldColumn ?? 'ONE_COLUMN',
        stepType: groupType,
        groupedFields: filteredGroupedFields,
        unGroupedFields: [],
        groupName: '',
        isFieldArray: false,
        sendEmail: false,
        minimunNumberOfGroups: 1,
      };
      const data = optionData;

      onChangeConditionalField({ [fieldId]: { [fieldId]: data } });
    }, [groupedFields, groupType, fieldColumn, fieldId]);

    const validateFields = () => {
      const newErrors: string[] = [];
      let newFieldErrors: Record<string, { name?: string; id?: string }> = {};
      if (groupType === 'GROUPED') {
        (groupedFields ?? []).forEach((group) => {
          group.fields.forEach((field, idx) => {
            const fieldKey = `${group.id}-${idx}`;

            if (!newFieldErrors) newFieldErrors = {};

            if (!field.name || field.name.trim() === '') {
              newFieldErrors[fieldKey] = {
                ...newFieldErrors[fieldKey],
                name: 'Field name is required',
              };
            }

            if (!field.id || field.id.trim() === '') {
              newFieldErrors[fieldKey] = {
                ...newFieldErrors[fieldKey],
                id: 'Field ID is required',
              };
            } else if (currentStepFieldIds.includes(field.id)) {
              newFieldErrors[fieldKey] = {
                ...newFieldErrors[fieldKey],
                id: 'Field ID already exists',
              };
            }
          });
        });
      }

      setErrorFields(newErrors);
      setFieldErrors(newFieldErrors);

      const hasFieldErrors = Object.values(newFieldErrors).some(
        (fieldError) => !!fieldError.name || !!fieldError.id
      );

      return newErrors.length === 0 && !hasFieldErrors;
    };

    const handleSubmit = () => {
      if (!validateFields()) return null;

      const filteredGroupedFields =
        groupType === 'GROUPED'
          ? (groupedFields ?? []).map((group) => ({
              ...group,
              fields: filterInviteUserFields(group.fields),
            }))
          : [];

      const optionData = {
        columnType: fieldColumn,
        stepType: groupType,
        groupedFields: filteredGroupedFields,
        unGroupedFields: [],
        groupName: '',
        isFieldArray: false,
        sendEmail: false,
        minimunNumberOfGroups: 1,
      };
      const data = optionData;

      return { [fieldId]: data };
    };

    useImperativeHandle(ref, () => ({
      validate: validateFields,
      onSubmit: handleSubmit,
    }));

    const getColumnsCount = (type: string) =>
      type === 'ONE_COLUMN' ? 1 : type === 'TWO_COLUMNS' ? 2 : 3;

    const removeGroup = (groupIndex: number) => {
      setGroupedFields((prev) => prev.filter((_, i) => i !== groupIndex));
    };

    const renderFieldInputs = (
      fields: CreditApplicationFields[],
      groupIndex: number,
      onChange: <K extends keyof CreditApplicationFields>(
        fieldIndex: number,
        key: K,
        value: CreditApplicationFields[K]
      ) => void
    ) => {
      return fields.map((field, idx) => {
        const fieldKey =
          groupType === 'GROUPED' ? `${groupedFields[groupIndex]?.id}-${idx}` : `ungrouped-${idx}`;
        const idSuggestedOptions = filteredIdSuggestionsOptions(field.id);
        return (
          <div key={idx} className="w-full rounded GREY_300-BORDER border p-2">
            <div className="w-full mt-2 mb-4">
              <AppInputField
                isRequired
                placeholder="Enter field name"
                label="Field Name"
                value={field.name}
                errorText={fieldErrors?.[fieldKey]?.name || ''}
                onTextChange={(text) => {
                  onChange(idx, 'name', text);
                }}
              />
            </div>
            <div className="w-full mt-2 mb-4 relative" ref={wrapperRef}>
              <AppInputField
                isRequired
                placeholder="Enter field id"
                label="Field Id"
                value={field.id}
                onFocus={() => setIsIdAutoCompleteOpen(`focus-id-${fieldKey}`)}
                onMouseUp={() => {
                  setIsIdAutoCompleteOpen(`focus-id-${fieldKey}`);
                }}
                errorText={fieldErrors?.[fieldKey]?.id || ''}
                onBlur={() => {
                  if (field.id?.trim() !== field.id) {
                    onChange(idx, 'id', field.id.trim());
                  }
                }}
                onTextChange={(text) => {
                  onChange(idx, 'id', text);
                  setFieldErrors((prev) => ({
                    ...prev,
                    [fieldKey]: {
                      ...prev?.[fieldKey],
                      id: '',
                    },
                  }));
                }}
              />
              {isIdAutoCompleteOpen === `focus-id-${fieldKey}` && idSuggestedOptions.length > 0 && (
                <div className="absolute w-full mt-1 bg-white border rounded-lg shadow-md z-50">
                  <div className="p-2 max-h-60 overflow-y-auto">
                    {idSuggestedOptions.map((suggestedOption, i) => (
                      <div
                        key={`${suggestedOption}_${i}-id-option`}
                        onMouseDown={() => {
                          onChange(idx, 'id', suggestedOption);
                          setIsIdAutoCompleteOpen('');
                        }}
                        className="p-1 hover:bg-gray-100 cursor-pointer text-xs">
                        {suggestedOption}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="w-full my-2">
              <AppDropDown
                label="Field Type"
                isRequired
                value={field.fromField}
                items={FIELD_TYPE.map((type) => ({
                  label: type.replaceAll('_', ' '),
                  value: type,
                }))}
                onSelectChange={(item) => {
                  if (item) {
                    onChange(idx, 'fromField', item.value);
                    onChange(idx, 'isRequired', false);
                    onChange(idx, 'isHashed', false);
                    if (field.fromField === 'INVITE_USER') {
                      onChange(idx, 'minimumCreditAmount', 0);
                      onChange(idx, 'type', 'AUTHORIZED_AGENT');
                    }
                  }
                }}
              />
            </div>
            {field.fromField === 'CONDITIONAL_OPTION' && (
              <div className="w-full my-2">
                <AppDropDown
                  label="Option Type"
                  isRequired
                  value={field.optionType ?? ''}
                  items={[
                    { label: 'Text', value: 'text' },
                    { label: 'File', value: 'file' },
                  ]}
                  onSelectChange={(item) => {
                    if (item) {
                      onChange(idx, 'optionType', item.value);
                    }
                  }}
                />
              </div>
            )}
            {field.fromField === 'DATE' && (
              <div className="my-2 flex flex-col">
                <AppInputField
                  label="Default Value"
                  value={field.defaultValue != null ? field.defaultValue : ''}
                  onTextChange={(text) => {
                    onChange(idx, 'defaultValue', text);
                  }}
                />
              </div>
            )}
            {(field.fromField === 'TEXT' ||
              field.fromField === 'NUMBER' ||
              field.fromField === 'CURRENCY') && (
              <>
                <div className="my-2">
                  <AppInputField
                    label="Prefix"
                    value={field.prefix}
                    onTextChange={(text) => {
                      onChange(idx, 'prefix', text);
                    }}
                  />
                </div>
                <div className="my-2">
                  <AppInputField
                    label="Suffix"
                    value={field.suffix}
                    onTextChange={(text) => {
                      onChange(idx, 'suffix', text);
                    }}
                  />
                </div>
              </>
            )}
            <div className="my-2 flex flex-col">
              <AppInputField
                label="Linked Field Id"
                value={field.linkedFieldId !== undefined ? field?.linkedFieldId : ''}
                onTextChange={(text) => {
                  onChange(idx, 'linkedFieldId', text);
                }}
              />
            </div>
            {field.fromField === 'CONDITIONAL_OPTION' && (
              <div className="my-2 flex flex-col">
                <AppInputField
                  label="Secondary Text"
                  value={field.secondaryText || ''}
                  onTextChange={(text) => {
                    onChange(idx, 'secondaryText', text);
                  }}
                />
              </div>
            )}
            {(field.fromField === 'OPTION_FIELD' || field.fromField === 'CHECKBOX_LIST') && (
              <div className="my-2 flex flex-col">
                <AppInputField
                  label="Option List"
                  value={field.optionList || ''}
                  onTextChange={(text) => {
                    onChange(idx, 'optionList', text);
                  }}
                />
                <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                  Add option list separed by comma
                </p>
              </div>
            )}
            {field.fromField === 'DROPDOWN' && (
              <div className="my-2 flex flex-col">
                <AppInputField
                  label="Options"
                  value={field.optionList || ''}
                  onTextChange={(text) => {
                    onChange(idx, 'optionList', text);
                  }}
                />
                <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                  Add option list separated by &quot;::&quot;
                </p>
              </div>
            )}
            {(field.id.includes('supplier-branches') || field.id.includes('supplier-sales-reps')) &&
              (field.fromField === 'OPTION_FIELD' ||
                field.fromField === 'CHECKBOX_LIST' ||
                field.fromField === 'DROPDOWN') && (
                <div className="my-2 flex flex-col gap-2">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <AppDropDown
                        label="Value to save"
                        value={field.optionValueSource ?? 'NAME'}
                        items={[
                          { label: 'Name', value: 'NAME' },
                          { label: 'ERP ID', value: 'ERP_ID' },
                        ]}
                        onSelectChange={(item) => {
                          if (item)
                            onChange(idx, 'optionValueSource', item.value as 'NAME' | 'ERP_ID');
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <AppDropDown
                        label="Label to show"
                        value={field.optionLabelSource ?? 'NAME'}
                        items={[
                          { label: 'Name', value: 'NAME' },
                          { label: 'ERP ID', value: 'ERP_ID' },
                        ]}
                        onSelectChange={(item) => {
                          if (item)
                            onChange(idx, 'optionLabelSource', item.value as 'NAME' | 'ERP_ID');
                        }}
                      />
                    </div>
                  </div>
                  <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                    ERP ID avoids ambiguity if two branches/reps share a name. Value and label are
                    independent — e.g. save the ERP ID while still showing the name.
                  </p>
                </div>
              )}
            {field.id.includes('supplier-sales-reps') &&
              (field.fromField === 'OPTION_FIELD' ||
                field.fromField === 'CHECKBOX_LIST' ||
                field.fromField === 'DROPDOWN') && (
                <div className="my-2 flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <DillCheckbox
                      isChecked={field.houseRepsOnly ?? false}
                      onChange={(checked: boolean) => onChange(idx, 'houseRepsOnly', checked)}
                    />
                    <span
                      className="text-sm cursor-pointer"
                      onClick={() => onChange(idx, 'houseRepsOnly', !field.houseRepsOnly)}>
                      Only show House Reps
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <AppInputField
                        label="Field label when a House Rep is selected"
                        value={field.houseRepFieldLabel || ''}
                        placeholder="e.g. House Account Id"
                        onTextChange={(text) => onChange(idx, 'houseRepFieldLabel', text)}
                      />
                    </div>
                    <div className="flex-1">
                      <AppInputField
                        label="Field label otherwise"
                        value={field.nonHouseRepFieldLabel || ''}
                        placeholder="e.g. Sales Rep Id"
                        onTextChange={(text) => onChange(idx, 'nonHouseRepFieldLabel', text)}
                      />
                    </div>
                  </div>
                  <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                    Leave both blank to keep the Field Name above fixed regardless of which rep is
                    selected.
                  </p>
                </div>
              )}
            {field.fromField === 'OPTION_FIELD' && (
              <div className="my-2 flex flex-col">
                <AppInputField
                  label="Option Descriptions"
                  value={field.optionDescriptions || ''}
                  onTextChange={(text) => {
                    onChange(idx, 'optionDescriptions', text);
                  }}
                  inputType="textarea"
                />
                <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                  Optional. Add one description per option, separated by &quot;;;&quot;, in the same
                  order as the Option List above.
                </p>
              </div>
            )}
            {field.fromField === 'INVITE_USER' && (
              <div className="my-2 flex items-center">
                <AppDropDown
                  label="Type of signer"
                  isRequired
                  value={field?.type ?? ''}
                  errorText={errorFields.includes('groupType') ? 'Please select' : ''}
                  items={[
                    'AUTHORIZED_AGENT',
                    'GUARANTOR',
                    'OTHER',
                    'PERSONAL',
                    'CORPORATE_GUARANTOR',
                  ].map((type) => ({
                    label: type.replaceAll('_', ' '),
                    value: type,
                  }))}
                  onSelectChange={(item) => {
                    if (item) {
                      onChange(
                        idx,
                        'type',
                        item.value as
                          | 'AUTHORIZED_AGENT'
                          | 'GUARANTOR'
                          | 'OTHER'
                          | 'PERSONAL'
                          | 'CORPORATE_GUARANTOR'
                      );
                    }
                  }}
                />
              </div>
            )}
            {field.fromField === 'INVITE_USER' && (
              <div className="my-2 flex flex-col">
                <AppInputField
                  isRequired
                  label="Who needs to sign"
                  value={field.whoNeedsToSign || ''}
                  onTextChange={(text) => {
                    onChange(idx, 'whoNeedsToSign', text);
                  }}
                />
                <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                  Add titles of who needs to sign separed by comma
                </p>
              </div>
            )}
            {field.fromField === 'INVITE_USER' && (
              <div className="my-2 flex flex-col">
                <AppInputField
                  isRequired
                  label="Minimum number of signatures"
                  value={field.minimumNumberOfSignatures || ''}
                  onTextChange={(text) => {
                    onChange(idx, 'minimumNumberOfSignatures', text);
                  }}
                />
              </div>
            )}
            {field.fromField !== 'DOCUMENT_FIELD' &&
              field.fromField !== 'INVITE_USER' &&
              field.fromField !== 'BANK_REFERENCE' && (
                <div className="my-2 flex items-center">
                  <AppRadioButton
                    isChecked={field.isRequired}
                    onClick={(val: boolean) => {
                      onChange(idx, 'isRequired', val);
                    }}
                  />
                  <p
                    className="text-sm ml-1 cursor-pointer"
                    onClick={() => {
                      onChange(idx, 'isRequired', !field.isRequired);
                    }}>
                    Is Field Required
                  </p>
                </div>
              )}
            {field.fromField === 'BANK_REFERENCE' && (
              <div className="my-2 flex items-center">
                <AppRadioButton
                  isChecked={field.onlyShowPlaidOption}
                  onClick={(val: boolean) => {
                    onChange(idx, 'onlyShowPlaidOption', val);
                  }}
                />
                <p
                  className="text-sm ml-1 cursor-pointer"
                  onClick={() => {
                    onChange(idx, 'onlyShowPlaidOption', !field.onlyShowPlaidOption);
                  }}>
                  Show only plaid option
                </p>
              </div>
            )}
            {field.fromField === 'TEXT' && (
              <div className="my-2 flex items-center">
                <AppRadioButton
                  isChecked={field.isFieldEmail || false}
                  onClick={(val: boolean) => {
                    onChange(idx, 'isFieldEmail', val);
                    if (!val) onChange(idx, 'emailValidationType', undefined);
                  }}
                />
                <p
                  className="text-sm ml-1 cursor-pointer"
                  onClick={() => {
                    const next = !field.isFieldEmail;
                    onChange(idx, 'isFieldEmail', next);
                    if (!next) onChange(idx, 'emailValidationType', undefined);
                  }}>
                  Is field an email
                </p>
              </div>
            )}
            {field.fromField === 'TEXT' && field.isFieldEmail && (
              <div className="my-2">
                <AppDropDown
                  label="Email Validation Type"
                  value={field.emailValidationType ?? 'SINGLE'}
                  items={[
                    { label: 'Single email', value: 'SINGLE' },
                    { label: 'Multiple emails (comma or semicolon separated)', value: 'MULTIPLE' },
                  ]}
                  onSelectChange={(item) => {
                    if (item)
                      onChange(idx, 'emailValidationType', item.value as 'SINGLE' | 'MULTIPLE');
                  }}
                />
              </div>
            )}
            {(field.fromField === 'TEXT' || field.fromField === 'NUMBER') && (
              <div className="my-2 flex items-center">
                <AppRadioButton
                  isChecked={field.isHashed || false}
                  onClick={(val: boolean) => {
                    onChange(idx, 'isHashed', val);
                  }}
                />
                <p
                  className="text-sm ml-1 cursor-pointer"
                  onClick={() => {
                    onChange(idx, 'isHashed', !field.isHashed);
                  }}>
                  Is Field Hashed
                </p>
              </div>
            )}
            {(field.fromField === 'INVITE_USER' && field?.type === 'GUARANTOR') ||
              (field?.type === 'CORPORATE_GUARANTOR' && (
                <div className="my-2 flex items-center">
                  <AppInputField
                    label="Minimum Credit Amount"
                    value={field?.minimumCreditAmount?.toString() ?? ''}
                    onTextChange={(text) => onChange(idx, 'minimumCreditAmount', Number(text))}
                    inputType="number"
                    isRequired
                    placeholder="Enter minimum credit amount"
                    onBlur={() => {
                      if (field?.minimumCreditAmount && field?.minimumCreditAmount < 1) {
                        onChange(idx, 'minimumCreditAmount', 1);
                      }
                    }}
                    errorText={
                      errorFields.includes('minimumCreditAmount')
                        ? 'Minimum credit amount is required'
                        : ''
                    }
                  />
                </div>
              ))}
            {field.fromField === 'DOCUMENT_FIELD' && (
              <div className="my-2 flex items-center flex-col">
                <AppInputField
                  inputType="textarea"
                  label="Document Text"
                  value={field.documentText || ''}
                  onTextChange={(text) => {
                    onChange(idx, 'documentText', text);
                  }}
                />
              </div>
            )}
            {field.fromField === 'DOCUMENT_FIELD' && (
              <div className="my-2 flex items-center flex-col">
                <AppInputField
                  label="Font Size"
                  value={field.fontSize || ''}
                  onTextChange={(text) => {
                    onChange(idx, 'fontSize', text);
                  }}
                />
              </div>
            )}
            {field.fromField === 'DOCUMENT_FIELD' && (
              <div className="my-2 flex items-center flex-col justify-start">
                <p>Font Weight</p>
                <RadioInputs
                  id={`fontWeight-${idx}`}
                  selectedOption={field.fontWeight || 'font-normal'}
                  options={['font-thin', 'font-normal', 'font-semibold', 'font-bold']}
                  onChange={({ name, value }) => {
                    onChange(idx, 'fontWeight', value);
                  }}
                  className="gap-2 flex items-center"
                />
              </div>
            )}
            {(field.fromField === 'DOCUMENT_FIELD' || field.fromField === 'CHECK_BOX') && (
              <div className="my-2 flex items-center flex-col justify-start">
                <p>How are the fields aligned in the document? (LEFT, RIGHT, CENTER)</p>
                <RadioInputs
                  id={`alignment-${idx}`}
                  selectedOption={field.alignment || 'LEFT'}
                  options={['LEFT', 'CENTER', 'RIGHT']}
                  onChange={({ name, value }) => {
                    onChange(idx, 'alignment', value as 'LEFT' | 'RIGHT' | 'CENTER');
                  }}
                  className="gap-2 flex items-center"
                />
              </div>
            )}
            {field.fromField === 'OPTION_FIELD' && (
              <div className="my-2 flex items-center">
                <AppRadioButton
                  isChecked={field.isRow || false}
                  onClick={(val: boolean) => {
                    onChange(idx, 'isRow', val);
                  }}
                />
                <p
                  className="text-sm ml-1 cursor-pointer"
                  onClick={() => {
                    onChange(idx, 'isRow', !field.isRow);
                  }}>
                  Are options in a row or column
                </p>
              </div>
            )}
            <div className="my-2 flex items-center">
              <AppRadioButton
                isChecked={field.hidden || false}
                onClick={(val: boolean) => {
                  onChange(idx, 'hidden', val);
                }}
              />
              <p
                className="text-sm ml-1 cursor-pointer"
                onClick={() => {
                  onChange(idx, 'hidden', !field.hidden);
                }}>
                Hide field when filling application
              </p>
            </div>
          </div>
        );
      });
    };

    return (
      <div className="w-full border rounded p-2 flex gap-2 flex-col border-gray-200 border rounded-lg">
        {groupType === 'GROUPED' && (
          <>
            {(groupedFields ?? []).map((field, i) => {
              return (
                <div key={field.id} className="w-full rounded GREY_300-BORDER border p-2 my-2">
                  <AppDropDown
                    label="Number of Columns"
                    isRequired
                    value={field.columnType}
                    items={COLUMN_TYPES.map((c) => ({
                      label: c.replaceAll('_', ' '),
                      value: c,
                    }))}
                    onSelectChange={(item) => {
                      if (!item) return;

                      setGroupedFields((prev) => {
                        const groups = [...prev];

                        const group = groups[i];
                        if (group) {
                          const columnCount = getColumnsCount(item.value);

                          const newFields = Array(columnCount)
                            .fill(null)
                            .map((_, indx) => {
                              return (
                                group.fields[indx] || {
                                  name: '',
                                  id: '',
                                  fromField: 'TEXT',
                                  isRequired: false,
                                  prefix: '',
                                  suffix: '',
                                }
                              );
                            });

                          groups[i] = {
                            ...group,
                            columnType: item.value,
                            fields: newFields,
                          };
                        }

                        return groups;
                      });
                    }}
                  />

                  <div className="flex gap-2 mt-2">
                    {renderFieldInputs(field.fields, i, (fieldIdx, key, val) => {
                      setGroupedFields((prev) => {
                        const groupArray = [...prev];
                        const targetGroup = { ...groupArray[i] };
                        const targetFields = [...(targetGroup.fields || [])];
                        const existing = targetFields[fieldIdx] || {
                          name: '',
                          id: '',
                          fromField: '',
                          isRequired: false,
                          prefix: '',
                          suffix: '',
                        };
                        targetFields[fieldIdx] = { ...existing, [key]: val };
                        targetGroup.fields = targetFields;
                        groupArray[i] = targetGroup;
                        return groupArray;
                      });
                    })}
                  </div>
                  <div
                    className="w-full cursor-pointer flex justify-end my-2 text-red-500 items-center"
                    onClick={() => removeGroup(i)}>
                    <div className="text-sm mr-1">Remove</div>
                    <Trash color={COLORS.ERROR_400} size={16} />
                  </div>
                </div>
              );
            })}
            <div className="my-2">
              <AppButton
                text="Add Field"
                onClick={() =>
                  setGroupedFields((prev) => {
                    const groupArray = [...prev];

                    groupArray.push({
                      id: `field-${groupArray.length + 1}`,
                      index: groupArray.length + 1,
                      columnType: 'ONE_COLUMN',
                      fields: [
                        {
                          name: '',
                          id: '',
                          fromField: 'TEXT',
                          isRequired: false,
                          prefix: '',
                          suffix: '',
                        },
                      ],
                      groupedPosition: 0,
                    });

                    return groupArray;
                  })
                }
                type="PRIMARY"
                buttonStyles={{ width: '100px', height: '35px' }}
              />
            </div>
          </>
        )}
      </div>
    );
  }
);

export default FormDocumentFields;
